import { Card, Group, Space, Text, Title } from "@mantine/core";
import { numericFormatter } from "react-number-format";
import { NoChartDataWrapper } from "../../../../mantine/charts/components/NoChartDataWrapper/NoChartDataWrapper";
import { MantineBarChart } from "../../../Charts/BarChart/MantineBarChart";
import type { EnergyRevenuesListItem } from "../../RevenueMonitoring.types";
import { TotalsSideBar } from "../../TotalsSideBar/TotalsSideBar";
import { REVENUE_TABLE_HEADER_TEXTS } from "../EnergyRevenuesTable/EnergyRevenuesTable";

const SERIES = [
  {
    label: REVENUE_TABLE_HEADER_TEXTS.vermarktung_volume,
    name: "vermarktung_volume",
    color: "#118EB0"
  },
  {
    label: REVENUE_TABLE_HEADER_TEXTS.market_premium_volume,
    name: "market_premium_volume",
    color: "#42B8B2"
  },
  {
    label: REVENUE_TABLE_HEADER_TEXTS.negative_price_volume,
    name: "negative_price_volume",
    color: "#4D4C7E"
  }
];

const INACTIVE_SERIES = ["negative_price_volume"];

const UNIT = " kWh";

interface EnergyVolumeOverviewChartProps {
  formattedData: Array<
    Pick<
      EnergyRevenuesListItem,
      | "vermarktung_volume"
      | "market_premium_volume"
      | "negative_price_volume"
      | "period_start"
    >
  >;
  isLoading?: boolean;
}

function EnergyVolumeOverviewChart({
  formattedData,
  isLoading
}: EnergyVolumeOverviewChartProps) {
  const sums = formattedData.reduce(
    (acc, item) => ({
      vermarktung_volume:
        acc.vermarktung_volume + (item.vermarktung_volume ?? 0),
      market_premium_volume:
        acc.market_premium_volume + (item.market_premium_volume ?? 0),
      negative_price_volume:
        acc.negative_price_volume + (item.negative_price_volume ?? 0)
    }),
    {
      vermarktung_volume: 0,
      market_premium_volume: 0,
      negative_price_volume: 0
    }
  );

  const totals = {
    vermarktung_volume: formattedData.some(
      (item) => item.vermarktung_volume !== null
    )
      ? sums.vermarktung_volume
      : null,
    market_premium_volume: formattedData.some(
      (item) => item.market_premium_volume !== null
    )
      ? sums.market_premium_volume
      : null,
    negative_price_volume: formattedData.some(
      (item) => item.negative_price_volume !== null
    )
      ? sums.negative_price_volume
      : null
  };

  return (
    <Card w="100%" withBorder>
      <Title order={6}>Strommengenübersicht in kWh</Title>
      <Text>
        Hier sehen Sie die erzeugten Strommengen sowie die Mengen, für die Sie
        Anrecht auf Marktprämie haben.
      </Text>
      <Space h="lg" />
      <Group align="center" wrap="nowrap">
        <NoChartDataWrapper
          isLoading={isLoading}
          noData={formattedData.length === 0}
        >
          <MantineBarChart
            data={formattedData}
            dataKey="period_start"
            defaultInactiveSeries={INACTIVE_SERIES}
            legendPosition="top"
            series={SERIES}
            tooltipProps={{
              formatter: (value) =>
                numericFormatter(value.toString(), {
                  decimalScale: 2,
                  decimalSeparator: ",",
                  thousandSeparator: ".",
                  fixedDecimalScale: true
                })
            }}
            unit={UNIT}
            withLegend
            xAxisProps={{
              interval: 1
            }}
            yAxisProps={{
              tickFormatter: (value) =>
                numericFormatter((value / 1000).toString(), {
                  decimalScale: 2,
                  decimalSeparator: ",",
                  suffix: "k",
                  thousandSeparator: ".",
                  fixedDecimalScale: true
                })
            }}
          />
        </NoChartDataWrapper>

        <TotalsSideBar
          totalsSeries={[
            ...SERIES.map((series) => ({
              label: series.label,
              value: totals[series.name],
              color: series.color
            }))
          ]}
          unit={UNIT}
        />
      </Group>
    </Card>
  );
}

export { EnergyVolumeOverviewChart, EnergyVolumeOverviewChartProps };
